import {
  GiftCouponRedeemStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import { fmtTime, redeemStatusLabel } from '../components/couponDetail/constants';

// ─────────────────────────────────────────────
// 주문 1건의 "신청자 번호" 목록을 엑셀로 내보낸다.
// 셀 스타일은 커뮤니티 xlsx 미지원이라, 지원 범위(열 너비·행 높이·헤더 고정)로만
// 가독성을 확보한다.
// ─────────────────────────────────────────────

const HEADER_ROW_PT = 28;
const BODY_ROW_PT = 22;

/** 컬럼 폭 자동 조정 (한글 2.2칸/영문 1칸 기준) */
function autoWidth(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const cols: XLSX.ColInfo[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let max = 8;
    for (let R = range.s.r; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell?.v == null) continue;
      let w = 0;
      for (const ch of String(cell.v)) w += ch.charCodeAt(0) > 0x7f ? 2.2 : 1;
      if (w > max) max = w;
    }
    cols.push({ wch: Math.min(Math.ceil(max) + 4, 60) });
  }
  ws['!cols'] = cols;
}

/** 헤더는 높게, 데이터 행은 넉넉히 */
function setRowHeights(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const rows: XLSX.RowInfo[] = [];
  for (let R = range.s.r; R <= range.e.r; R++) {
    rows[R] = { hpt: R === 0 ? HEADER_ROW_PT : BODY_ROW_PT };
  }
  ws['!rows'] = rows;
}

/** 헤더 행(1행) 고정 */
function finishSheet(ws: XLSX.WorkSheet) {
  autoWidth(ws);
  setRowHeights(ws);
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  if (!ws['!views']) ws['!views'] = [];
  (ws['!views'] as Array<Record<string, unknown>>)[0] = { state: 'frozen', ySplit: 1 };
}

/**
 * 가운데 4자리 마스킹 — 010-****-5678.
 * 하이픈·자릿수 방어 (11자리 3-4-4, 10자리 3-3-4, 그 외는 뒤 4자리 기준).
 */
const maskMiddlePhone = (raw?: string): string => {
  const d = (raw ?? '').replace(/\D/g, '');
  if (d.length === 11) return `${d.slice(0, 3)}-****-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-****-${d.slice(6)}`;
  if (d.length >= 7) return `${d.slice(0, 3)}-****-${d.slice(-4)}`;
  return raw ?? '';
};

/**
 * 신청자 번호 — "나중에 받기"(recipient) 우선, 재발행/연장 발송분은
 * additional/renewal 수신번호로 폴백한다.
 */
const applicantPhone = (c: GiftCouponSummary): string =>
  c.recipient?.phoneNumber ||
  c.additionalRecipientPhone ||
  c.renewalRecipientPhone ||
  '';

/** 선택상품명 — selectedChoiceUnit이 populate된 경우에만 이름을 반환(미선택/미populate는 빈값) */
const choiceUnitName = (c: GiftCouponSummary): string =>
  c.selectedChoiceUnit && typeof c.selectedChoiceUnit === 'object'
    ? c.selectedChoiceUnit.name ?? ''
    : '';

/** 취소·폐기(번호가 무의미해진 상태)가 아닌 쿠폰 */
const isExportable = (c: GiftCouponSummary): boolean =>
  c.redeemStatus !== GiftCouponRedeemStatus.Canceled &&
  c.redeemStatus !== GiftCouponRedeemStatus.Disposed;

/**
 * 내보내기 대상 — 신청자 번호가 있는 쿠폰 중 취소·폐기를 제외.
 * 나중에 사용하기(Available+번호)·번호 인증(나중에받기)·발송완료(Delivered)를 모두 포함한다.
 * 이 목록이 비면 버튼을 비활성화한다.
 */
export const getDeliveredCouponPhoneTargets = (
  coupons: GiftCouponSummary[],
): GiftCouponSummary[] => coupons.filter((c) => !!applicantPhone(c) && isExportable(c));

/** 미발송 포함(번호 기입 폼) 대상 — 취소·폐기만 제외한 전체 쿠폰 */
export const getPhoneFormTargets = (
  coupons: GiftCouponSummary[],
): GiftCouponSummary[] => coupons.filter(isExportable);

export interface DeliveredPhoneExcelOptions {
  /**
   * 미발송(신청 번호 없는) 쿠폰도 포함해 "번호 기입 폼"으로 내보낸다.
   * 쿠폰 순서는 그대로 두고, 미발송 건의 수령번호 칸에 기입 마커를 넣어
   * 채워서 회신받는 용도.
   */
  includeUnsent?: boolean;
}

/**
 * 주문 1건의 신청자 번호를 엑셀로 내보낸다. 수령번호는 가운데 4자리를 ****로 마스킹.
 * (프로젝트 표준: 커뮤니티 xlsx + file-saver, 정산 엑셀들과 동일)
 */
export const exportDeliveredCouponPhoneExcel = (
  order: GiftOrderForAdmin | undefined,
  coupons: GiftCouponSummary[],
  options?: DeliveredPhoneExcelOptions,
): void => {
  const includeUnsent = options?.includeUnsent ?? false;
  // 기입 폼도 쿠폰 순서(순번)를 그대로 유지한다 — 미발송 건은 사이사이 마커로 강조.
  const targets = includeUnsent
    ? getPhoneFormTargets(coupons)
    : getDeliveredCouponPhoneTargets(coupons);

  // 선택상품·메모는 하나라도 값이 있을 때만 컬럼을 넣어 빈 컬럼이 생기지 않게 한다.
  const hasChoiceUnit = targets.some((c) => !!choiceUnitName(c));
  const hasMemo = targets.some((c) => !!c.memo?.trim());

  const rows = targets.map((c) => {
    const row: Record<string, string | number> = {
      // 주문 내 쿠폰 고유 순번(coupon.seq) — 화면의 #1, #2 표기와 동일
      쿠폰순번: c.seq != null ? `#${c.seq}` : '',
      쿠폰번호: c.code,
    };
    if (hasChoiceUnit) row.선택상품 = choiceUnitName(c);
    const phone = applicantPhone(c);
    // 미발송 건은 수령번호 칸에 기입 유도 마커를 넣어 "채워야 할 곳"이 한눈에 띄게 한다.
    // (셀 배경색은 커뮤니티 xlsx 미지원 — 텍스트 마커로 강조)
    row.수령번호 = phone ? maskMiddlePhone(phone) : '▶ 번호 기입';
    // 사용가능(Available)인데 번호가 있으면 "나중에 받기" 예약 건이므로 그렇게 표기.
    row.상태 = !phone
      ? '미발송'
      : c.redeemStatus === GiftCouponRedeemStatus.Available
        ? '나중에 받기'
        : redeemStatusLabel[c.redeemStatus];
    row.발송일시 = c.gifticon?.deliveredAt ? fmtTime(c.gifticon.deliveredAt) : '';
    if (hasMemo) row.메모 = c.memo?.trim() ?? '';
    return row;
  });

  const header = [
    '쿠폰순번',
    '쿠폰번호',
    '수령번호',
    ...(hasChoiceUnit ? ['선택상품'] : []),
    '상태',
    '발송일시',
    ...(hasMemo ? ['메모'] : []),
  ];
  const ws = XLSX.utils.json_to_sheet(rows, { header });
  finishSheet(ws);
  // 기입 폼은 상태 열 필터로 "미발송"만 쏙 골라볼 수 있게 자동필터를 건다.
  if (includeUnsent) ws['!autofilter'] = { ref: ws['!ref'] ?? 'A1' };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    ws,
    includeUnsent ? '번호 기입폼(미발송 포함)' : '발송완료 번호',
  );

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const ordererName = order?.receiverName ? `_${order.receiverName}` : '';
  saveAs(
    new Blob([out], { type: 'application/octet-stream' }),
    includeUnsent
      ? `발송번호기입폼_미발송포함${ordererName}.xlsx`
      : `발송완료번호${ordererName}.xlsx`,
  );
};
